/**
 * „Wymagane" — a question the client cannot send the form without answering.
 *
 * ── A GREY GLYPH, and it overrides the canvas ────────────────────────────────────────────────
 * KANON `01-ankiety.html` draws it as a 20px ember-edged square at radius 6 with an ember lock
 * inside — an `.ibtn` wearing the accent. The owner looked at it in the running app on
 * 17 September 2026 and refused it: „szare, nie takie rozjechane kwadratowe". So it is a 16px lock
 * glyph in `--text-3`, with no border and no fill, standing beside the label like a piece of
 * punctuation.
 *
 * The reasoning agrees with the accent rule the rest of this app is held to. The mark appears on
 * EVERY required question, which on the intake's „Dane osobowe" is four of five rows and beside
 * three „→ Ciało" notes — the accent as a background rather than as a scalpel. The word „Wymagane"
 * travels with the glyph, so the colour was carrying nothing the shape and the word did not.
 *
 * Recorded in `docs/design/raporty-2026-09-17/README.md` as an owner decision overriding the export.
 *
 * ── The word is RENDERED, not an `aria-label` ────────────────────────────────────────────────
 * „Wymagane" stands off screen beside the glyph rather than naming a `role="img"`. Two reasons, and
 * the second is the one that decided it: `role="img"` swallows its own subtree, so the element then
 * has exactly one channel and a future change inside it is silently unreadable — and the mark is
 * required to carry the state as WORDS, which `e2e/reports-surveys.spec.ts` pins by looking for the
 * word rather than for a label („colour alone is not a state").
 *
 * ── Why a component rather than a class, and why it is SHARED ────────────────────────────────
 * It is a glyph plus a word, not a box, and passing the word in keeps `t` out of this file. It was
 * `/raporty`'s until 17 September 2026, when the owner asked for the same lock on the READ of a
 * filled survey: `features/survey-report` draws it beside every required question's label, on both
 * surfaces that show a report. Four consumers across a page, a feature and a shared kit is well
 * past the promotion rule's second one.
 */

import { LockIcon } from './AuthIcons';
import hidden from './visuallyHidden.module.css';

import styles from './RequiredMark.module.css';

export interface RequiredMarkProps {
  /** „Wymagane", from the caller's own `t`. */
  readonly label: string;
}

export function RequiredMark({ label }: RequiredMarkProps) {
  return (
    <span className={styles.requiredMark}>
      <LockIcon className={styles.requiredIcon} />
      <span className={hidden.srOnly}>{label}</span>
    </span>
  );
}
